import React, { useState, useEffect, useMemo } from 'react';
import { API, getToken } from './adminApi';
import MeetAnalyticsSection from './MeetAnalyticsSection';
import SignupAnalyticsSection from './SignupAnalyticsSection';

const RANGES = [1, 7, 14, 30, 90];

const PctCountToggle = ({ label, asCount, onChange }) => (
  <span style={{ marginLeft: '0.75rem', whiteSpace: 'nowrap' }}>
    <span className="as-card-meta">{label}: </span>
    <button className={`as-filter-tab ${!asCount ? 'as-filter-tab--active' : ''}`} onClick={() => onChange(false)}>%</button>
    <button className={`as-filter-tab ${asCount ? 'as-filter-tab--active' : ''}`} onClick={() => onChange(true)}>No.</button>
  </span>
);

const STEP_LABELS = {
  join: 'Clicked Join',
  demo: 'Tried the Demo',
  video: 'Watched the video',
  connect: 'Connect with members',
  more: 'Tapped scroll-down arrow',
  quickmatch: 'Used the quick-match card',
  signup: 'Sign-up / log-in link',
  legal: 'Privacy / Terms / Contact',
  cookies: 'Cookie banner',
  internal_link: 'Other page on the site',
  external_link: 'External link',
  other_click: 'Other click on page',
  navigated: 'Navigated away (back / menu)',
  left: 'Left / closed tab',
};

const CHART_METRICS = {
  visits: { label: 'Visits', key: 'visits' },
  uniqueVisitors: { label: 'Unique visitors', key: 'uniqueVisitors' },
  firstTimeVisitors: { label: 'First-time visitors (A/B log, pre-dates tracking)', key: 'firstTimeVisitors' },
};

const fmtDuration = (ms) => {
  if (!ms) return '0s';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
};
// Load timings need sub-second precision ("photo 1.4s", not "photo 1s").
const fmtSecs = (ms) => (ms == null ? '—' : ms < 10000 ? `${(ms / 1000).toFixed(1)}s` : fmtDuration(ms));
const fmtDay = (k) => new Date(`${k}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const fmtTime = (d) => new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
// How long before `to` the time `from` was: "40 min", "5 h", "3 days".
const fmtGapBefore = (from, to) => {
  const mins = Math.round((new Date(to) - new Date(from)) / 60000);
  if (mins < 60) return `${mins} min`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)} h`;
  return `${Math.round(mins / 1440)} days`;
};

const StatTile = ({ label, value, sub }) => (
  <div className="as-pa-tile">
    <div className="as-pa-tile-label">{label}</div>
    <div className="as-pa-tile-value">{value}</div>
    {sub && <div className="as-pa-tile-sub">{sub}</div>}
  </div>
);

// "1 h 20m", "3 days 4h" — for gaps longer than a page visit.
const fmtLongGap = (ms) => {
  if (!ms) return '0m';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ${String(mins % 60).padStart(2, '0')}m`;
  return `${Math.floor(hours / 24)} days ${hours % 24}h`;
};

const SPREAD_ROWS = [
  { key: 'timeOnPage', label: 'Time on page', fmt: fmtDuration },
  { key: 'scroll', label: 'Scroll depth', fmt: (v) => `${v}%` },
  { key: 'timeToFirstClick', label: 'Time to first click', fmt: fmtDuration, note: 'visits that clicked anything' },
  { key: 'timeToJoin', label: 'Time to Join click', fmt: fmtDuration, note: 'visits that clicked Join' },
  { key: 'firstPaint', label: 'Page appeared', fmt: fmtSecs },
  { key: 'heroLoaded', label: 'Photo shown', fmt: fmtSecs },
  { key: 'visitsPerVisitor', label: 'Returning: visits per visitor', fmt: (v) => `${v}`, unit: 'visitors', hint: 'How many times each visitor came in this period (coming back 30+ minutes later counts as another visit).' },
  { key: 'timeToReturn', label: 'Returning: time before coming back', fmt: fmtLongGap, unit: 'returns', hint: 'How long visitors were away before each return in this period (30 minutes or more).' },
];

// Min / quartiles / median / mean / 90th / max for one measure at a time
// (picked from the dropdown), plus that measure's histogram — the averages
// alone hide how uneven visits are. Open to start; click the title to fold.
const SpreadCard = ({ spread, timeBuckets, histograms }) => {
  const [open, setOpen] = useState(true);
  const [measure, setMeasure] = useState('timeOnPage');
  const row = SPREAD_ROWS.find(r => r.key === measure);
  const d = spread[measure];
  // The picked measure's histogram, if it has one.
  const buckets = (histograms && histograms[measure]) || (measure === 'timeOnPage' ? timeBuckets : null) || [];
  const maxBucket = Math.max(1, ...buckets.map(b => b.count));
  const total = buckets.reduce((a, b) => a + b.count, 0);
  return (
    <div className="as-pa-card">
      <div className="as-pa-card-head">
        <h2 className="as-section-title as-pa-click-row" onClick={() => setOpen(o => !o)} style={{ cursor: 'pointer' }}>
          Spread <span style={{ color: '#6366f1', fontSize: '0.75rem' }}>{open ? '▲' : '▼'}</span>
        </h2>
        {open && (
          <select className="as-pa-select" value={measure} onChange={e => setMeasure(e.target.value)} aria-label="Measure">
            {SPREAD_ROWS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        )}
      </div>
      {open && (<>
        <p className="as-pa-hint">
          {row.hint && <>{row.hint} </>}
          Lower / upper quartile: a quarter of {row.unit || 'visits'} were below / above it, so half sit between the two.
          90th: only 1 in 10 {row.unit || 'visits'} went past it.{measure === 'timeOnPage' && ' Visits over 3 minutes are left out as outliers (a page left open, not reading).'}
          {row.note && ` Counts only ${row.note}.`}
        </p>
        {!d?.n ? <div className="as-act-empty">No visits with this measure in range</div> : (
          <div className="as-pa-tiles">
            {d.returned != null && <StatTile label="Came back" value={`${d.returned} of ${d.n}`} sub={`${d.n ? Math.round(d.returned / d.n * 1000) / 10 : 0}% of visitors`} />}
            <StatTile label="Median" value={row.fmt(d.median)} sub={`mean ${row.fmt(d.mean)}`} />
            <StatTile label="Range" value={`${row.fmt(d.min)} – ${row.fmt(d.max)}`} sub={`${d.n} ${row.unit || 'visits'}`} />
            <StatTile label="Middle half" value={`${row.fmt(d.p25)} – ${row.fmt(d.p75)}`} sub="lower – upper quartile" />
            <StatTile label="90th percentile" value={row.fmt(d.p90)} />
          </div>
        )}
        {total > 0 && buckets.map(b => (
          <div key={b.label} className="as-pa-hbar-row">
            <div className="as-pa-hbar-label">{b.label}</div>
            <div className="as-pa-hbar-track">
              <div className="as-pa-hbar" style={{ width: `${(b.count / maxBucket) * 100}%` }} />
            </div>
            <div className="as-pa-hbar-value">{b.count} <span>({Math.round(b.count / total * 100)}%)</span></div>
          </div>
        ))}
      </>)}
    </div>
  );
};

// Single-series daily bar chart, hand-drawn SVG with a per-bar hover tooltip.
const DailyChart = ({ daily, metricKey }) => {
  const [hover, setHover] = useState(null);
  const W = 720, H = 220, PAD_L = 36, PAD_R = 8, PAD_T = 12, PAD_B = 28;
  const plotW = W - PAD_L - PAD_R, plotH = H - PAD_T - PAD_B;
  const values = daily.map(d => d[metricKey]);
  const rawMax = Math.max(1, ...values);
  const step = Math.max(1, Math.ceil(rawMax / 4));
  const max = step * 4;
  const slot = plotW / daily.length;
  const gap = daily.length > 60 ? 1 : 2;
  const barW = Math.max(1, slot - gap);
  const y = (v) => PAD_T + plotH - (v / max) * plotH;
  const labelEvery = Math.ceil(daily.length / 8);

  const barPath = (x, v) => {
    const top = y(v), bottom = PAD_T + plotH;
    const h = bottom - top;
    if (h <= 0) return '';
    const r = Math.min(4, barW / 2, h);
    return `M${x},${bottom} V${top + r} Q${x},${top} ${x + r},${top} H${x + barW - r} Q${x + barW},${top} ${x + barW},${top + r} V${bottom} Z`;
  };

  const hd = hover != null ? daily[hover] : null;
  return (
    <div className="as-pa-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${metricKey} per day`} onMouseLeave={() => setHover(null)}>
        {[0, 1, 2, 3, 4].map(i => (
          <g key={i}>
            <line x1={PAD_L} x2={W - PAD_R} y1={y(step * i)} y2={y(step * i)} className={i === 0 ? 'as-pa-axis' : 'as-pa-grid'} />
            <text x={PAD_L - 6} y={y(step * i) + 4} textAnchor="end" className="as-pa-tick">{step * i}</text>
          </g>
        ))}
        {daily.map((d, i) => {
          const x = PAD_L + i * slot + gap / 2;
          return (
            <g key={d.day}>
              <path d={barPath(x, d[metricKey])} className={`as-pa-bar ${hover === i ? 'as-pa-bar--hover' : ''}`} />
              {/* full-height hit target, wider than the bar */}
              <rect x={PAD_L + i * slot} y={PAD_T} width={slot} height={plotH} fill="transparent" onMouseEnter={() => setHover(i)} />
              {(i % labelEvery === 0 || i === daily.length - 1) && (
                <text x={x + barW / 2} y={H - 8} textAnchor="middle" className="as-pa-tick">{fmtDay(d.day)}</text>
              )}
            </g>
          );
        })}
      </svg>
      {hd && (
        <div className="as-pa-tooltip" style={{ left: `${((PAD_L + hover * slot + slot / 2) / W) * 100}%` }}>
          <strong>{fmtDay(hd.day)}</strong>
          <div>Visits <b>{hd.visits}</b></div>
          <div>Unique visitors <b>{hd.uniqueVisitors}</b></div>
          <div>Bounces <b>{hd.bounces}</b>{hd.visits ? ` (${Math.round(hd.bounces / hd.visits * 100)}%)` : ''}</div>
          <div>Avg time <b>{fmtDuration(hd.avgTimeMs)}</b> · median <b>{fmtDuration(hd.medianTimeMs)}</b></div>
          <div>First-time (A/B log) <b>{hd.firstTimeVisitors}</b></div>
        </div>
      )}
    </div>
  );
};

// Quick-match hero A/B (community.heroFlow / heroFlowABTest): quick-match
// card vs normal hero side by side, then the searches run on the card.
const QM_ROWS = [
  { key: 'visits', label: 'Visits' },
  { key: 'uniqueVisitors', label: 'Unique visitors' },
  { key: 'bounceRate', label: 'Bounce rate', fmt: v => `${v}%` },
  { key: 'medianTimeMs', label: 'Median time on page', fmt: v => fmtDuration(v) },
  { key: 'avgScroll', label: 'Average scroll depth', fmt: v => `${v}%` },
  { key: 'medianScroll', label: 'Median scroll depth', fmt: v => `${v}%` },
  { key: 'scrollIqr', label: 'Scroll depth, middle half (25th–75th percentile)', fmt: v => `${v[0]}% – ${v[1]}%` },
  { key: 'scrollP90', label: 'Scroll depth, top 10% reached (90th percentile)', fmt: v => `${v}%` },
  { key: 'scrolledPastHero', label: 'Scrolled past the hero (25%+)', fmt: v => `${v}%` },
  { key: 'usedCard', label: 'Tapped the card', qmOnly: true },
  // Quick match C: the two opening buttons…
  { key: 'introJoin', label: 'C: clicked "Join and get introduced"', choiceOnly: true },
  { key: 'introLook', label: 'C: clicked "See who\'s near me first"', choiceOnly: true },
  // …then the search (B and C) and what came of it.
  { key: 'searched', label: 'Ran a search', qmOnly: true },
  { key: 'foundSome', label: 'C: search found members', choiceOnly: true },
  { key: 'foundNone', label: 'C: search found no one near', choiceOnly: true },
  { key: 'widened', label: 'C: widened their search', choiceOnly: true },
  { key: 'emailed', label: 'Gave their email', qmOnly: true },
  { key: 'connect', label: 'Tapped Connect on a member', qmOnly: true },
  { key: 'joinedAfterLook', label: 'C: clicked Join after looking first', choiceOnly: true },
  { key: 'joined', label: 'Clicked Join / sign up' },
  { key: 'signups', label: 'Signed up' },
  { key: 'signupRate', label: 'Signup rate (of visitors)', fmt: v => `${v}%` },
];

const QuickMatchABCard = ({ quickMatch }) => {
  const [open, setOpen] = useState(true);
  // '' = all quick-match communities, or one community's id.
  const [only, setOnly] = useState('');
  const picked = only ? quickMatch.byCommunity?.find(c => c.communityId === only) : null;
  const searches = only ? quickMatch.searches.filter(s => s.communityId === only) : quickMatch.searches;
  // Two tests, each within its own time: now A vs Quick match C, earlier A
  // vs Quick match B (the server splits A's visits by date too).
  const tests = (picked ? picked.tests : quickMatch.tests) || [];
  const FLOW = {
    default: { title: 'Normal hero (A)' },
    quickmatch: { title: 'Quick match B', card: true },
    choice: { title: 'Quick match C', card: true, choice: true },
  };
  const fmtDay = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return (
    <div className="as-pa-card">
      <div className="as-pa-card-head">
        <h2 className="as-section-title" onClick={() => setOpen(o => !o)} style={{ cursor: 'pointer' }}>
          Hero test: A vs Quick match C <span style={{ color: '#6366f1', fontSize: '0.75rem' }}>{open ? '▲' : '▼'}</span>
        </h2>
      </div>
      {open && (<>
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
          <select className="as-pa-select" value={only} onChange={e => setOnly(e.target.value)} aria-label="Community">
            <option value="">All quick-match communities</option>
            {(quickMatch.byCommunity || []).map(c => <option key={c.communityId} value={c.communityId}>{c.title}</option>)}
          </select>
          <span className="as-pa-hint" style={{ margin: 0 }}>Visitors are split half and half: now between the normal hero (A) and Quick match C; before 25 Oct, between A and Quick match B.</span>
        </div>
        {tests.length === 0 && <p className="as-pa-hint">No visits in this period.</p>}
        {tests.map(t => {
          const byFlow = Object.fromEntries(t.variants.map(v => [v.flow, v]));
          const cols = t.variants.map(v => ({ flow: v.flow, ...FLOW[v.flow] })).sort((x, y) => (x.card ? 1 : 0) - (y.card ? 1 : 0)); // A first
          const cell = (col, r) => {
            const v = (byFlow[col.flow] || {})[r.key];
            if ((r.qmOnly && !col.card) || (r.choiceOnly && !col.choice) || v == null) return '—';
            return r.fmt ? r.fmt(v) : v;
          };
          return (
            <div key={t.id} style={{ marginTop: '0.8rem', opacity: t.id === 'earlier' ? 0.75 : 1 }}>
              <h3 className="as-section-title" style={{ fontSize: '0.95rem', margin: '0 0 0.3rem' }}>
                {t.title} <span style={{ fontWeight: 400, color: '#64748b' }}>({t.from ? `since ${fmtDay(t.from)}` : `until ${fmtDay(t.until)}`})</span>
              </h3>
              <div className="as-act-table-wrap">
                <table className="as-act-table">
                  <thead><tr><th></th>{cols.map(c => <th key={c.flow}>{c.title}</th>)}</tr></thead>
                  <tbody>
                    {QM_ROWS.filter(r => !r.choiceOnly || byFlow.choice).map(r => (
                      <tr key={r.key}>
                        <td>{r.label}</td>
                        {cols.map(c => <td key={c.flow} className="as-pa-num">{c.card ? <strong>{cell(c, r)}</strong> : cell(c, r)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
        <h2 className="as-section-title" style={{ marginTop: '1rem' }}>Searches on the card ({searches.length})</h2>
        <div className="as-act-table-wrap">
          <table className="as-act-table">
            <thead><tr><th>Time</th><th>Community</th><th>Where</th><th>Their answers</th><th>Matches shown</th><th>Email</th></tr></thead>
            <tbody>
              {searches.length === 0 && <tr><td colSpan="6" className="as-act-empty">None yet</td></tr>}
              {searches.map((s, i) => (
                <tr key={i}>
                  <td className="as-act-time">{fmtTime(s.createdAt)}</td>
                  <td>{s.communityTitle}</td>
                  <td>{s.localArea || '—'}</td>
                  <td style={{ fontSize: '0.85rem' }}>
                    {Object.entries(s.answers).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' · ') || '—'}
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>
                    <strong>{s.matchCount}</strong>
                    {(s.shown || []).map(m => (
                      <div key={m.userId} title={`userId ${m.userId}`}>
                        {m.firstName}{m.age != null ? `, ${m.age}` : ''}{m.localArea ? ` · ${m.localArea}` : ''}{m.miles != null ? ` · ${m.miles} mi away` : ''}{' '}
                        <span className="as-card-meta">#{m.userId.slice(-6)}</span>
                      </div>
                    ))}
                  </td>
                  <td>{s.email || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>)}
    </div>
  );
};

const CommunityAnalyticsTab = () => {
  const [days, setDays] = useState(1);
  const [communityId, setCommunityId] = useState('');
  const [includeInternal, setIncludeInternal] = useState(false);
  // '' all visits | 'shown' had the "Watch the video" button | 'hidden' didn't
  const [video, setVideo] = useState('');
  const [chartMetric, setChartMetric] = useState('visits');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [sort, setSort] = useState({ key: 'visits', dir: -1 });
  // Join click (% of visits ↔ visits that clicked Join) and Signups
  // (count ↔ % of unique visitors) can each be flipped between % and number.
  const [joinAsCount, setJoinAsCount] = useState(false);
  const [signupsAsCount, setSignupsAsCount] = useState(true);
  const [returningAsCount, setReturningAsCount] = useState(true);
  const [demoAsCount, setDemoAsCount] = useState(false);
  const [openVisit, setOpenVisit] = useState(null);
  // Older visits loaded with "Show more", appended after data.recent.
  const [moreVisits, setMoreVisits] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => { fetchData(); }, [days, communityId, includeInternal, video]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    setFetchError('');
    try {
      const token = await getToken();
      const qs = new URLSearchParams({ days: String(days) });
      if (communityId) qs.set('communityId', communityId);
      if (includeInternal) qs.set('includeInternal', '1');
      if (video) qs.set('video', video);
      const res = await fetch(`${API}/admin/community-analytics?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      setData(json);
      setMoreVisits([]);
      setHasMore(json.recent.length < json.totalVisits);
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const visits = data ? [...data.recent, ...moreVisits] : [];

  const loadMore = async () => {
    if (!visits.length) return;
    setLoadingMore(true);
    try {
      const token = await getToken();
      const qs = new URLSearchParams({ days: String(days), before: visits[visits.length - 1].createdAt, limit: '100' });
      if (communityId) qs.set('communityId', communityId);
      if (includeInternal) qs.set('includeInternal', '1');
      const res = await fetch(`${API}/admin/community-analytics/visits?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      setMoreVisits(prev => [...prev, ...json.visits]);
      setHasMore(json.hasMore);
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  const sortedCommunities = useMemo(() => {
    if (!data) return [];
    return [...data.perCommunity].sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      return typeof av === 'string' ? av.localeCompare(bv) * sort.dir : (av - bv) * sort.dir;
    });
  }, [data, sort]);

  const joinKey = joinAsCount ? 'joinClicks' : 'joinClickRate';
  const signupsKey = signupsAsCount ? 'signups' : 'signupRate';
  const demoKey = demoAsCount ? 'demoClicks' : 'demoClickRate';
  const returningKey = returningAsCount ? 'returningVisitors' : 'returningRate';
  // Keep the active sort when its column flips between % and number.
  const SORT_PAIRS = { joinClicks: 'joinClickRate', joinClickRate: 'joinClicks', signups: 'signupRate', signupRate: 'signups', demoClicks: 'demoClickRate', demoClickRate: 'demoClicks', returningVisitors: 'returningRate', returningRate: 'returningVisitors' };
  const flipSortKey = (from, to) => setSort(prev => (prev.key === from ? { ...prev, key: to } : prev));
  const setJoinMode = (asCount) => { setJoinAsCount(asCount); flipSortKey(SORT_PAIRS[asCount ? 'joinClicks' : 'joinClickRate'], asCount ? 'joinClicks' : 'joinClickRate'); };
  const setDemoMode = (asCount) => { setDemoAsCount(asCount); flipSortKey(SORT_PAIRS[asCount ? 'demoClicks' : 'demoClickRate'], asCount ? 'demoClicks' : 'demoClickRate'); };
  const setSignupsMode = (asCount) => { setSignupsAsCount(asCount); flipSortKey(SORT_PAIRS[asCount ? 'signups' : 'signupRate'], asCount ? 'signups' : 'signupRate'); };
  const setReturningMode = (asCount) => { setReturningAsCount(asCount); flipSortKey(SORT_PAIRS[asCount ? 'returningVisitors' : 'returningRate'], asCount ? 'returningVisitors' : 'returningRate'); };
  const sortBy = (key) => setSort(s => ({ key, dir: s.key === key ? -s.dir : (key === 'title' ? 1 : -1) }));
  const sortMark = (key) => (sort.key === key ? (sort.dir === -1 ? ' ▼' : ' ▲') : '');

  const s = data?.summary;
  const totalSteps = data ? data.nextSteps.reduce((a, b) => a + b.count, 0) : 0;
  const maxStep = data ? Math.max(1, ...data.nextSteps.map(n => n.count)) : 1;

  return (
    <div className="as-activity as-pa">
      {fetchError && <div className="as-error">{fetchError}</div>}

      <div className="as-activity-toolbar as-pa-toolbar">
        <select className="as-pa-select" value={communityId} onChange={e => setCommunityId(e.target.value)} aria-label="Community">
          <option value="">All communities</option>
          {(data?.communities || []).map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
        </select>
        {RANGES.map(d => (
          <button key={d} className={`as-filter-tab ${days === d ? 'as-filter-tab--active' : ''}`} onClick={() => setDays(d)}>{d}d</button>
        ))}
        <select className="as-pa-select" value={video} onChange={e => setVideo(e.target.value)} aria-label="Video" title="For communities with a &quot;Watch the video&quot; — compare visits that had the button with those that didn't (A/B test or not)">
          <option value="">Video: all visits</option>
          <option value="shown">Video button shown</option>
          <option value="hidden">No video button</option>
        </select>
        <label className="as-pa-check">
          <input type="checkbox" checked={includeInternal} onChange={e => setIncludeInternal(e.target.checked)} />
          Include admin, localhost &amp; ad-preview visits
        </label>
        <button className="as-filter-tab" onClick={fetchData}>↻ Refresh</button>
      </div>

      {loading && !data && <div className="as-loading">Loading…</div>}

      {data && (<>
        {!data.trackingStarted && (
          <div className="as-pa-note">
            No page-view data yet. Tracking starts recording once this version is deployed — until then,
            only the "First-time visitors" chart (from the existing A/B log) has history.
          </div>
        )}
        {data.trackingStarted && (
          <div className="as-pa-note as-pa-note--quiet">
            Tracking since {fmtTime(data.trackingStarted)}. Bounce = under 10s, no clicks, and scrolled less than 25% of the page.
            Time on page counts only while the tab is visible.
            {data.hiddenLoads > 0 && <> {data.hiddenLoads} automated/hidden page load{data.hiddenLoads === 1 ? '' : 's'} (never on screen, e.g. Meta ad review) excluded.</>}
          </div>
        )}

        <p className="as-pa-hint">
          ⏱ Time on page leaves out visits over 3 minutes as outliers — with no scrolling or clicks, they're nearly always a page left open
          (e.g. a phone left on it), not someone reading. {s.timeOutliers ?? 0} visit{(s.timeOutliers ?? 0) === 1 ? '' : 's'} left out of the time figures in this period;
          they still count as visits, bounces, scroll and joins.
        </p>
        <div className="as-pa-tiles">
          <StatTile label="Visits" value={s.visits} sub={`${s.returningVisits} returning (${s.visits ? Math.round(s.returningVisits / s.visits * 1000) / 10 : 0}%)`} />
          <StatTile label="Unique visitors" value={s.uniqueVisitors} />
          <StatTile label="Avg time on page" value={fmtDuration(s.avgTimeMs)} sub={`median ${fmtDuration(s.medianTimeMs)} · excl. ${s.timeOutliers ?? 0} over 3m`} />
          <StatTile label="Bounce rate" value={`${s.bounceRate}%`} sub={`avg scroll ${s.avgScroll}%`} />
          {s.legalClicks > 0 && (
            <StatTile label="Privacy / Terms" value={`${s.legalClicks} visits`} sub={`Privacy ${s.privacyClicks ?? 0} · Terms ${s.termsClicks ?? 0} · Contact ${s.contactClicks ?? 0}`} />
          )}
          {(s.cookieAccepted > 0 || s.cookieDeclined > 0) && (
            <StatTile label="Cookie banner" value={`${s.cookieAccepted} accepted`} sub={`${s.cookieDeclined} declined · not counted as a click`} />
          )}
          {s.videoOpened > 0 && (
            <StatTile label="Video" value={`${s.videoOpened} opened`} sub={`${s.videoPlayed ?? 0} played · ${s.videoHalf ?? 0} watched half · ${s.videoFinished ?? 0} finished`} />
          )}
          <StatTile
            label="Clicked Join"
            value={joinAsCount ? s.joinClicks : `${s.joinClickRate}%`}
            sub={joinAsCount ? `${s.joinClickRate}% · Demo ${s.demoClicks ?? 0} (${s.demoClickRate}%)` : `${s.joinClicks} of ${s.visits} visits · Demo ${s.demoClicks ?? 0} (${s.demoClickRate}%)`}
          />
          <StatTile
            label="Signed up"
            value={signupsAsCount ? s.signups : `${s.signupRate}%`}
            sub={signupsAsCount ? `${s.signupRate}% of visitors` : `${s.signups} of ${s.uniqueVisitors} visitors`}
          />
          <StatTile
            label="Photo shown (median)"
            value={s.timedVisits ? fmtSecs(s.medianHeroMs) : '—'}
            sub={s.timedVisits ? `page at ${fmtSecs(s.medianFirstPaintMs)} · ${s.leftBeforePhoto} of ${s.timedVisits} left before photo` : 'recorded from 4 Oct'}
          />
        </div>

        <div className="as-pa-card">
          <div className="as-pa-card-head">
            <h2 className="as-section-title">Per day</h2>
            <select className="as-pa-select" value={chartMetric} onChange={e => setChartMetric(e.target.value)} aria-label="Chart metric">
              {Object.entries(CHART_METRICS).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </select>
          </div>
          <DailyChart daily={data.daily} metricKey={CHART_METRICS[chartMetric].key} />
        </div>

        <div className="as-pa-split">
          <div className="as-pa-card">
            <h2 className="as-section-title">What they did next</h2>
            <p className="as-pa-hint">First thing each visit did on the page, or how it ended if nothing was clicked.</p>
            {data.nextSteps.length === 0 && <div className="as-act-empty">No visits in range</div>}
            {data.nextSteps.map(n => (
              <div key={n.step} className="as-pa-hbar-row">
                <div className="as-pa-hbar-label">{STEP_LABELS[n.step] || n.step}</div>
                <div className="as-pa-hbar-track">
                  <div className="as-pa-hbar" style={{ width: `${(n.count / maxStep) * 100}%` }} />
                </div>
                <div className="as-pa-hbar-value">{n.count} <span>({Math.round(n.count / totalSteps * 100)}%)</span></div>
              </div>
            ))}
          </div>

          <div className="as-pa-card">
            <h2 className="as-section-title">Where they came from</h2>
            <table className="as-act-table">
              <tbody>
                {data.referrers.length === 0 && <tr><td className="as-act-empty">—</td></tr>}
                {data.referrers.map(r => <tr key={r.key}><td>{r.key}</td><td className="as-pa-num">{r.count}</td></tr>)}
              </tbody>
            </table>
            <h2 className="as-section-title" style={{ marginTop: '1rem' }}>Device</h2>
            <table className="as-act-table">
              <tbody>
                {data.devices.map(r => <tr key={r.key}><td>{r.key}</td><td className="as-pa-num">{r.count}</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>

        {data.spread && <SpreadCard spread={data.spread} timeBuckets={data.timeBuckets} histograms={data.histograms} />}

        {data.quickMatch && <QuickMatchABCard quickMatch={data.quickMatch} />}

        {!communityId && (<>
          <div className="as-pa-card-head" style={{ marginTop: '1.5rem' }}>
            <h2 className="as-section-title">By community ({sortedCommunities.length})</h2>
            <div>
              <PctCountToggle label="Join click" asCount={joinAsCount} onChange={setJoinMode} />
              <PctCountToggle label="Demo click" asCount={demoAsCount} onChange={setDemoMode} />
              <PctCountToggle label="Signups" asCount={signupsAsCount} onChange={setSignupsMode} />
              <PctCountToggle label="Returning" asCount={returningAsCount} onChange={setReturningMode} />
            </div>
          </div>
          <div className="as-act-table-wrap">
            <table className="as-act-table as-pa-sortable">
              <thead>
                <tr>
                  <th onClick={() => sortBy('title')}>Community{sortMark('title')}</th>
                  <th onClick={() => sortBy('visits')}>Visits{sortMark('visits')}</th>
                  <th onClick={() => sortBy('uniqueVisitors')}>Unique{sortMark('uniqueVisitors')}</th>
                  <th onClick={() => sortBy('avgTimeMs')}>Avg time{sortMark('avgTimeMs')}</th>
                  <th onClick={() => sortBy('medianTimeMs')}>Median time{sortMark('medianTimeMs')}</th>
                  <th onClick={() => sortBy('bounceRate')}>Bounce{sortMark('bounceRate')}</th>
                  <th onClick={() => sortBy('avgScroll')}>Avg scroll{sortMark('avgScroll')}</th>
                  <th onClick={() => sortBy(joinKey)}>{joinAsCount ? 'Join clicks' : 'Join click'}{sortMark(joinKey)}</th>
                  <th onClick={() => sortBy(demoKey)}>{demoAsCount ? 'Demo clicks' : 'Demo click'}{sortMark(demoKey)}</th>
                  <th onClick={() => sortBy(signupsKey)}>{signupsAsCount ? 'Signups' : 'Signup %'}{sortMark(signupsKey)}</th>
                  <th onClick={() => sortBy(returningKey)} title="Visitors who came back (another visit after 30+ minutes away), or had been before">{returningAsCount ? 'Returning' : 'Returning %'}{sortMark(returningKey)}</th>
                </tr>
              </thead>
              <tbody>
                {sortedCommunities.length === 0 && <tr><td colSpan="11" className="as-act-empty">None</td></tr>}
                {sortedCommunities.map(c => (
                  <tr key={c.communityId} className="as-pa-click-row" onClick={() => setCommunityId(c.communityId)}>
                    <td><strong>{c.title}</strong></td>
                    <td className="as-pa-num">{c.visits}</td>
                    <td className="as-pa-num">{c.uniqueVisitors}</td>
                    <td className="as-pa-num">{fmtDuration(c.avgTimeMs)}</td>
                    <td className="as-pa-num">{fmtDuration(c.medianTimeMs)}</td>
                    <td className="as-pa-num">{c.bounceRate}%</td>
                    <td className="as-pa-num">{c.avgScroll}%</td>
                    <td className="as-pa-num">{joinAsCount ? c.joinClicks : `${c.joinClickRate}%`}</td>
                    <td className="as-pa-num">{demoAsCount ? (c.demoClicks ?? 0) : `${c.demoClickRate}%`}</td>
                    <td className="as-pa-num">{signupsAsCount ? c.signups : `${c.signupRate}%`}</td>
                    <td className="as-pa-num">{returningAsCount ? (c.returningVisitors ?? 0) : `${c.returningRate ?? 0}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>)}

        <h2 className="as-section-title" style={{ marginTop: '1.5rem' }}>Recent visits ({visits.length} of {data.totalVisits ?? visits.length})</h2>
        <div className="as-act-table-wrap">
          <table className="as-act-table">
            <thead><tr><th>Time</th><th>Community</th><th>Visitor</th><th>From</th><th>Time on</th><th>Scroll</th><th>Loaded</th><th>Next</th></tr></thead>
            <tbody>
              {visits.length === 0 && <tr><td colSpan="8" className="as-act-empty">None</td></tr>}
              {visits.map(v => {
                const isOpen = openVisit === v._id;
                return (
                  <React.Fragment key={v._id}>
                    <tr className="as-pa-click-row" onClick={() => setOpenVisit(isOpen ? null : v._id)}>
                      <td className="as-act-time">{fmtTime(v.createdAt)}</td>
                      <td>{v.communityTitle}</td>
                      <td className="as-act-who">
                        {v.visitorId} · {v.device || '?'}
                        {/* Returning: how many times before, and how long before this
                            visit each of the latest earlier ones was (any page, any date). */}
                        {v.isReturning && (
                          <div className="as-card-meta" title={v.previousVisitSameCommunity === false ? 'Last visit was to a different page' : undefined}>
                            <span className="as-pa-tag">returning{v.visitsBefore ? ` ×${v.visitsBefore}` : ''}</span>
                            {v.earlierVisitAts?.length > 0 && (
                              <> {v.earlierVisitAts.map(t => fmtGapBefore(t, v.createdAt)).join(' · ')} before{v.visitsBefore > v.earlierVisitAts.length ? ' …' : ''}</>
                            )}
                            {v.visitsBefore > 1 && v.firstVisitAt && (
                              <> · every {fmtGapBefore(new Date(0), new Date((new Date(v.createdAt) - new Date(v.firstVisitAt)) / v.visitsBefore))} on avg</>
                            )}
                          </div>
                        )}
                        {v.isLoggedIn && <span className="as-pa-tag">logged in</span>}
                        {v.converted && <span className="as-pa-tag as-pa-tag--good">✓ signed up</span>}
                        {v.internal && <span className="as-pa-tag">{v.adPreview ? 'ad preview' : 'admin'}</span>}
                      </td>
                      <td>{v.utmSource ? `utm:${v.utmSource}` : (v.referrer || '(direct)')}</td>
                      <td className="as-pa-num">{fmtDuration(v.activeMs)}</td>
                      <td className="as-pa-num">{v.maxScroll}%</td>
                      <td className="as-pa-num" style={{ whiteSpace: 'nowrap' }}>
                        {!v.timed ? '—' : v.heroLoadedMs == null || v.heroLoadedMs > v.activeMs
                          ? <span className="as-pa-tag as-pa-tag--warn">left before photo</span>
                          : `photo ${fmtSecs(v.heroLoadedMs)}`}
                      </td>
                      <td>
                        {STEP_LABELS[v.nextStep] || v.nextStep}
                        {v.bounced && <span className="as-pa-tag">bounce</span>}
                        <span style={{ color: '#6366f1', fontSize: '0.75rem' }}> {isOpen ? '▲' : '▼'}</span>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan="8" className="as-pa-timeline">
                          {[
                            { t: -1, text: `0:00 — landed${v.referrer && v.referrer !== '(in-site)' ? ` from ${v.referrer}` : ''}` },
                            v.firstPaintMs != null && { t: v.firstPaintMs, text: `${fmtSecs(v.firstPaintMs)} — page appeared on screen` },
                            v.heroLoadedMs != null && { t: v.heroLoadedMs, text: `${fmtSecs(v.heroLoadedMs)} — hero photo loaded` },
                            ...v.actions.map(a => ({ t: a.atMs || 0, text: `${fmtDuration(a.atMs)} — ${STEP_LABELS[a.type] || a.type}${a.label ? `: "${a.label}"` : ''}${a.target ? ` → ${a.target}` : ''}` })),
                            { t: Number.MAX_SAFE_INTEGER, text: `${fmtDuration(v.activeMs)} — ${v.nextPath ? `went to ${v.nextPath}` : 'left the site / closed tab'} (max scroll ${v.maxScroll}%)${v.timed && v.heroLoadedMs == null ? ' — hero photo never loaded' : ''}` },
                          ].filter(Boolean).sort((a, b) => a.t - b.t).map((e, i) => <div key={i}>{e.text}</div>)}
                          {/* Then, on the "Find a group" page: what they searched / opened. */}
                          {(v.groupSearches || []).map((g, i) => (
                            <div key={`gs${i}`}>
                              {fmtDuration(g.afterMs)} after landing — {g.kind === 'open'
                                ? `opened "${g.openedTitle}"${g.via === 'join' ? ' (clicked Join)' : ''}${g.query ? ` from search "${g.query}"` : ''}`
                                : `searched ${g.query ? `"${g.query}"` : ''}${g.filters.length ? ` [${g.filters.join(', ')}]` : ''} — ${g.resultCount ?? '?'} group${g.resultCount === 1 ? '' : 's'} found`}
                            </div>
                          ))}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {hasMore && visits.length > 0 && (
          <div style={{ textAlign: 'center', margin: '0.75rem 0 1.5rem' }}>
            <button className="as-filter-tab" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : `Show ${Math.min(100, (data.totalVisits ?? 0) - visits.length) || 100} more`}
            </button>
          </div>
        )}
      </>)}

      <SignupAnalyticsSection days={days} communityId={communityId} includeInternal={includeInternal} />
      <MeetAnalyticsSection days={days} communityId={communityId} includeInternal={includeInternal} />
    </div>
  );
};

export default CommunityAnalyticsTab;
